import { Injectable, inject } from '@angular/core';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthStatus } from '@fpr/core/models/auth-status.models';
import { WindowRef } from './window.factory';
import { LocaleService } from './locale.service';
import { AuthenticatorJS } from '@fpr/types';

@Injectable({
    providedIn: 'root'
})
export class AuthStatusService {
    private readonly localeService = inject(LocaleService);
    private readonly window = inject(WindowRef);


    private status = from(this.authenticate());

    getStatus() {
        return this.status;
    }

    getSwid() {
        return this.getStatus()
            .pipe(
                map(authStatus => authStatus.swid)
            );
    }

    getAuthToken() {
        return this.getStatus()
            .pipe(
                map(status => status.authToken)
            );
    }

    private async getAuthJs(): Promise<AuthenticatorJS> {
        const authJs: AuthenticatorJS = this.window.nativeWindow['PEP']?.['PROFILE_AUTH'];

        if (!authJs) {
            throw new Error('Authenticator is missing');
        }

        if (authJs.isInitialized) {
            return authJs;
        }

        return authJs
            .init({
                loadScript: true,
                langPref: 'en'
            })
            .then(() => authJs);
    }

    private async authenticate(): Promise<AuthStatus> {
        const status: AuthStatus = {
            isLoggedIn: false,
            swid: '',
            authToken: ''
        };

        try {
            const authJs = await this.getAuthJs();

            status.isLoggedIn = (authJs.isInitialized)
                ? await authJs.getLoggedInStatus()
                : false;

            const guest = (status.isLoggedIn)
                ? await authJs.getGuest()
                : null;

            status.swid = guest?.profile?.swid || '';
            status.authToken = guest?.token?.access_token || '';
        } catch (err) {
            throw new Error(`Failed to authenticate: ${err}`);
        }

        return status;
    }

}
